import React, { useEffect, useRef, useState } from 'react';
import { IconVolumeUp, IconVolumeOff, IconVolumeDown } from '../../../icons';
import { VideoJsPlayer } from 'video.js';
import theme from '../../../theme';
import { Icon } from '../../../styles';
import { FillVolumeUpRange, VolumeHoverDetector, VolumeRange, VolumeRangeWrapper, VolumeSectionContainer } from '../styles';
import { VOLUME_CHANGE_EVENTS } from '../PlayerServices';

// Playback starting as well as the volume events: loading a source drops the 'volumechange' still
// queued on the element. When an ad fails, video.js lifts the mute of its muted-autoplay retry
// just as the content source is loaded back in, so the icon kept the mute over content playing
// with sound - and the first click muted it.
const VOLUME_SYNC_EVENTS = [...VOLUME_CHANGE_EVENTS, 'playing', 'adplaying'];

const ControlBarVolumeController = ({ player }: { player: VideoJsPlayer }) => {
  const [isVolumeSectionHovered, setIsVolumeSectionHovered] = useState<boolean>(false);
  // A muted player has no sound whatever its volume is - a muted autoplay keeps the volume at
  // 1 - so the button and the slider follow the muted state as well.
  const [volume, setVolume] = useState<number>(player.muted() ? 0 : player.volume());
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const IS_IOS = /Macintosh/.test(navigator.userAgent);

  useEffect(() => {
    const handleVolumeChange = () => setVolume(player.muted() ? 0 : player.volume());

    player.on(VOLUME_SYNC_EVENTS, handleVolumeChange);
    return () => {
      player.off(VOLUME_SYNC_EVENTS, handleVolumeChange);
    };
  }, [player]);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   player.on('volumechange', () => {
  //     !IS_IOS && setVolume(player.volume());
  //     IS_IOS && setVolume(player.muted() ? 0 : 1);
  //   });
  //
  //   return () => {
  //     player.off('volumechange');
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [player]);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
    setIsTablet(1250 > Number(window.innerWidth));
  };

  const handleChangeVolume = (volume) => {
    player.volume(volume);
    player.muted(volume === 0);
    // Set here as well as from the event: 'volumechange' is queued as a task, and waiting for it
    // makes the slider lag behind the drag.
    setVolume(volume);
  };

  const toggleMute = () => {
    player.muted(!player.muted());
    // Unmuting keeps the level the viewer had picked - only a player sitting at 0 has nothing to
    // come back to. Same rule as the M key in VideoPlayer.
    if (!player.muted() && !player.volume()) player.volume(1);
    setVolume(player.muted() ? 0 : player.volume());
  };
  return (
    <VolumeSectionContainer
      onMouseEnter={() => setIsVolumeSectionHovered(true)}
      onMouseLeave={() => setIsVolumeSectionHovered(false)}
      style={{
        marginRight: isMobile ? '0.75re' : '1.5rem',
      }}
    >
      <Icon cursor="pointer" size="semiLarge" onClick={toggleMute}>{VolumeIcon(volume)}</Icon>
      {(isVolumeSectionHovered && !isMobile) && !(IS_IOS && isTablet) && volume !== 0 &&
        <Volume value={volume} handleChange={handleChangeVolume} />}
    </VolumeSectionContainer>
  );
};

const VolumeIcon = (volume: number) => {
  if (volume === 0) return <IconVolumeOff />;
  else if (volume === 1) return <IconVolumeUp />;
  return <IconVolumeDown />;
};

const Volume = ({ handleChange, value }) => {
  return (
    <VolumeRangeWrapper id="volumeBar">
      <VolumeRange
        value={value}
        type="range"
        min="0"
        max="1"
        step="0.1"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.valueAsNumber)}
      />
      <FillVolumeUpRange volume={value} />
      <VolumeHoverDetector left={value} />
    </VolumeRangeWrapper>
  );
};

export default ControlBarVolumeController;
